import axios from 'axios'
import { clearSession, session } from './AuthSession'

const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_REQUEST_TIMEOUT) || 15000

const normalizeBaseURL = (value, fallback = '') => {
  const normalized = String(value || fallback || '').trim()
  if (!normalized) return ''
  return normalized.endsWith('/') ? normalized : `${normalized}/`
}

const normalizeServerMessage = (payload) => {
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message.trim()
  if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error.trim()
  if (typeof payload === 'string' && payload.trim()) return payload.trim()
  return null
}

const getSessionToken = () => {
  const sessionData = session()
  const token = sessionData && sessionData.token ? sessionData.token.token : null
  return typeof token === 'string' && token.trim() ? token.trim() : null
}

export const getUserFacingError = (error, fallbackMessage = 'Terjadi kesalahan. Silakan coba lagi.') => {
  if (!axios.isAxiosError(error)) {
    return {
      kind: 'unknown',
      message: error?.message || fallbackMessage,
      retryable: true,
      statusCode: null,
    }
  }

  const statusCode = Number(error.response?.status) || null
  const serverMessage = normalizeServerMessage(error.response?.data)

  if (error.code === 'ECONNABORTED') {
    return {
      kind: 'network',
      message: 'Permintaan memakan waktu terlalu lama. Silakan coba lagi.',
      retryable: true,
      statusCode,
    }
  }

  if (!error.response) {
    return {
      kind: 'network',
      message: 'Koneksi bermasalah. Periksa internet Anda lalu coba lagi.',
      retryable: true,
      statusCode: null,
    }
  }

  if (statusCode === 401) {
    return {
      kind: 'auth',
      message: serverMessage || 'Sesi Anda berakhir. Silakan login kembali.',
      retryable: false,
      statusCode,
    }
  }

  if (statusCode === 404) {
    return {
      kind: 'not_found',
      message: serverMessage || 'Data yang Anda cari tidak ditemukan.',
      retryable: false,
      statusCode,
    }
  }

  if (statusCode === 400 || statusCode === 409 || statusCode === 422) {
    return {
      kind: 'validation',
      message: serverMessage || fallbackMessage,
      retryable: false,
      statusCode,
    }
  }

  if (statusCode >= 500) {
    return {
      kind: 'server',
      message: serverMessage || fallbackMessage,
      retryable: true,
      statusCode,
    }
  }

  return {
    kind: 'unknown',
    message: serverMessage || fallbackMessage,
    retryable: true,
    statusCode,
  }
}

export const buildRequestError = (error, fallbackMessage) => {
  const normalized = getUserFacingError(error, fallbackMessage)
  const requestError = new Error(normalized.message)
  requestError.kind = normalized.kind
  requestError.retryable = normalized.retryable
  requestError.statusCode = normalized.statusCode
  requestError.originalError = error
  return requestError
}

const cmsHttp = axios.create({
  baseURL: normalizeBaseURL(process.env.REACT_APP_BASE_URL),
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json'
  }
})

cmsHttp.interceptors.request.use(
  (config) => {
    const token = getSessionToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    document.body.classList.add('loading-indicator')
    return config
  },
  (error) => Promise.reject(error)
)

cmsHttp.interceptors.response.use(
  (response) => {
    document.body.classList.remove('loading-indicator')
    return response
  },
  (error) => {
    document.body.classList.remove('loading-indicator')
    if (error.response?.status === 401) {
      clearSession()
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export const cmsRequest = (config) => cmsHttp.request(config)

export const cmsGet = (url, config = {}) => cmsRequest({ ...config, method: 'get', url })

export default cmsHttp